/**
 * MAHI — the session-entry animation ("the runway").
 *
 * Shown once per session, the first time someone lands on /discover. The four
 * letters walk in from the right in a staggered line, each overshooting its
 * mark and settling onto it, and the assembled wordmark then flies up into the
 * masthead slot it actually occupies in the Discover header.
 *
 * Three decisions are worth knowing before changing anything here.
 *
 * 1. It ends by *landing*, not by fading. The final frame is measured from the
 *    real header wordmark ([data-testid="mahi-wordmark"]) and the mark is
 *    animated onto it at the same size and the same colour, so when this
 *    unmounts there is nothing to dissolve — the identical masthead is already
 *    underneath. That is why the intro reads as the app assembling itself
 *    rather than as a splash screen getting out of the way, and it is also why
 *    the colour here must stay in step with MahiWordmark's `text-slate-700`.
 *
 * 2. The white ground fades on its own layer, and the mark never fades with
 *    it. Fading the two together is the obvious way to write this and it is
 *    wrong: the whole point of the landing is that the logo is still solid
 *    when it arrives, so the feed has to be revealed *behind* a moving mark.
 *    The one case that does fade the mark is the fallback below, where the
 *    masthead could not be measured and there is nothing to land on.
 *
 * 3. The faint "ghost" wordmark under the letters is not decoration. On a cold
 *    load the boot skeleton in public/index.html has already been showing an
 *    assembled MAHI, and MahiApp's route fallback keeps showing it while auth
 *    resolves; the ghost is that same mark, continuous, fading out as the real
 *    letters land on it. Without it the placeholder would blink out and the
 *    letters would march in over nothing, which reads as two logos in a row.
 *    It doubles as the marks-on-the-floor the letters walk to, so it earns its
 *    place on the in-app path (post-signup) where no placeholder preceded it.
 *
 * Timings are tuned as one sequence — see PHASE_MS. Total is ~1.2s, which is
 * the ceiling for something a returning user sees every session (see
 * mahiIntroGate.js). Everything animated is transform/opacity only, so it
 * composites on the GPU and does not touch layout on a mid-range phone.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MAHI_GLYPHS, MAHI_VIEWBOX } from './MahiLogo';
import { mahiLetterContainer as container, mahiLetterVariant as letter } from './mahiLetterMotion';
import useReducedMotion from '../../hooks/useReducedMotion';

const PHASE_MS = {
  // When the assembled mark leaves centre for the masthead. Must be after the
  // last letter has settled: delayChildren 120 + 3 * stagger 75 + 440 = 785.
  fly: 820,
  // Length of that flight; the component unmounts as it lands.
  flight: 360,
  // Reduced motion: no travel, no flight. Long enough to register that the
  // brand was there, then out.
  reducedHold: 140,
  reducedTotal: 420,
};

export default function MahiIntro({ onDone }) {
  const reduced = useReducedMotion();
  const markRef = useRef(null);
  const doneRef = useRef(false);
  // null while the mark is centre stage; then either a measured flight to the
  // masthead or, if there was nothing to measure, a plain fade.
  const [exit, setExit] = useState(null);

  // One finish path for every way this can end — the sequence completing, a
  // tap to skip, or the reduced-motion short version — so `onDone` never fires
  // twice and can never leave the feed thinking the intro is still up.
  const finish = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);

  useEffect(() => {
    if (reduced) {
      const toFade = setTimeout(() => setExit({ mode: 'fade' }), PHASE_MS.reducedHold);
      const toDone = setTimeout(finish, PHASE_MS.reducedTotal);
      return () => { clearTimeout(toFade); clearTimeout(toDone); };
    }
    const toFly = setTimeout(() => {
      // Measured, not hardcoded: the masthead's position depends on the
      // viewport and on the safe-area inset, and a guessed vector would land
      // the mark beside the header rather than on it. If the header isn't
      // there for any reason, fall back to a fade — a lesser ending, never a
      // broken one.
      const target = document.querySelector('[data-testid="mahi-wordmark"]')?.getBoundingClientRect();
      const source = markRef.current?.getBoundingClientRect();
      if (target?.width && source?.width) {
        setExit({
          mode: 'fly',
          x: (target.left + target.width / 2) - (source.left + source.width / 2),
          y: (target.top + target.height / 2) - (source.top + source.height / 2),
          scale: target.width / source.width,
        });
      } else {
        setExit({ mode: 'fade' });
      }
    }, PHASE_MS.fly);
    const toDone = setTimeout(finish, PHASE_MS.fly + PHASE_MS.flight);
    return () => { clearTimeout(toFly); clearTimeout(toDone); };
  }, [reduced, finish]);

  const markAnimation = exit?.mode === 'fly'
    ? { x: exit.x, y: exit.y, scale: exit.scale }
    : { opacity: exit ? 0 : 1 };

  return (
    <div
      // Above the Discover tour's z-[70]: on a brand-new account both are
      // pending at once, and the intro has to be the thing you see first.
      className="fixed inset-0 z-[80] flex items-center justify-center"
      // Any tap ends it. A first-time visitor has no investment yet, and an
      // animation you cannot dismiss is the one that gets resented.
      onPointerDown={finish}
      role="presentation"
    >
      {/* The ground, on its own layer — see note 2 above. */}
      <motion.div
        className="absolute inset-0 bg-white"
        initial={{ opacity: 1 }}
        animate={{ opacity: exit ? 0 : 1 }}
        transition={{ duration: 0.3, delay: exit ? 0.06 : 0, ease: 'easeOut' }}
      />

      {/* 8rem wide, matching the boot mark in public/index.html exactly, so a
          cold load hands over to this without the logo changing size. */}
      <motion.div
        ref={markRef}
        className="relative w-32 text-slate-700"
        animate={markAnimation}
        transition={{ duration: PHASE_MS.flight / 1000, ease: [0.4, 0, 0.2, 1] }}
      >
        <svg viewBox={MAHI_VIEWBOX} fill="currentColor" className="w-full h-auto" role="img" aria-label="MAHI">
          {reduced ? (
            <g>
              {MAHI_GLYPHS.map((glyph) => (
                <path key={glyph.id} transform={glyph.transform} d={glyph.d} />
              ))}
            </g>
          ) : (
            <>
              <motion.g
                // The marks on the floor. See note 3 above.
                initial={{ opacity: 0.35 }}
                animate={{ opacity: [0.35, 0.12, 0] }}
                transition={{ duration: 0.8, times: [0, 0.5, 1], ease: 'easeOut' }}
              >
                {MAHI_GLYPHS.map((glyph) => (
                  <path key={glyph.id} transform={glyph.transform} d={glyph.d} />
                ))}
              </motion.g>

              <motion.g variants={container} initial="hidden" animate="visible">
                {MAHI_GLYPHS.map((glyph) => (
                  <motion.g key={glyph.id} variants={letter}>
                    <path transform={glyph.transform} d={glyph.d} />
                  </motion.g>
                ))}
              </motion.g>
            </>
          )}
        </svg>
      </motion.div>
    </div>
  );
}
